import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveHospitalNotify,
  setMethod,
  setAlertType,
  setPotentialRegistry,
  setUpdateInputRows,
} from '../store/hospitalNotify.actions'
import { getHospitalNotifyOptionsSelector } from '../../../options/selectors'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { HospitalNotifyConstants } from '../store/hospitalNotify.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { getHospitalNotifySelector } from '../selectors'
import { useDispatch, useSelector } from 'react-redux'
import { IOption } from '../../../../common/common.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialHospitalNotifyState } from '../store/hospitalNotify.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'

const HospitalNotify: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const hospitalNotify = useSelector(getHospitalNotifySelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    method,
    alertType,
    potentialRegistry,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = hospitalNotify

  const hospitalNotifyOptions = useSelector(getHospitalNotifyOptionsSelector)
  const { methodOptions, alertOptions, potentialRegistryOptions } = hospitalNotifyOptions
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS
  const save = () => {
    dispatch(saveHospitalNotify())
  }

  const methodOnChange = (option: IOption) => {
    dispatch(setMethod(option))
    save()
  }

  const alertTypeOnChange = (option: IOption) => {
    dispatch(setAlertType(option))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(hospitalNotify, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialHospitalNotifyState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const dateTimeValidationCheck = initialStateCheck()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'hospitalNotify/SET_TIME', time: time })
              dispatch({ type: 'hospitalNotify/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: hospitalNotify.key,
                rowActionType: TimelineConstants.SET_HOSPITAL_NOTIFY_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }
  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: HospitalNotifyConstants.SET_METHOD,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('hospitalNotify.method'),
            dataTestID: 'Method',
            options: methodOptions,
            action: methodOnChange,
            listHeader: 'Method',
            selectedOption: method,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: HospitalNotifyConstants.SET_ALERT_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('hospitalNotify.alertType'),
            dataTestID: 'Alert Type',
            options: alertOptions,
            action: alertTypeOnChange,
            listHeader: 'Alert Type',
            selectedOption: alertType,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: HospitalNotifyConstants.SET_POTENTIAL_REGISTRY,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('hospitalNotify.potentialRegistry'),
            dataTestID: 'Potential Registry',
            options: potentialRegistryOptions,
            action: setPotentialRegistry,
            listHeader: 'Potential Registry',
            savedValues: potentialRegistry,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='HospitalNotifyPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Hospital Notify</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'hospitalNotify'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          comments={comments}
          attempts={attempts}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default HospitalNotify
